/**
 * Mastery Week 4 (W4) — chapter → KP id mapping derived from CURRICULUM.
 *
 * Pure, build-time-static lookup table. We compute it ONCE at module load
 * from `CURRICULUM` (`src/learn/curriculum/index.ts`) so the detector layer
 * stays pure (no runtime walk of the curriculum tree on every answer).
 *
 * INVARIANTS:
 *   - pure module; no DOM / localStorage / game/ imports.
 *   - `excluded` field reserved for future「課外延伸」 KPs (`tag: 'extension'`)
 *     — the KPEntry shape currently has no such field, so every entry derived
 *     from CURRICULUM ships with `excluded: false`. Adding the field later
 *     does NOT require changes to chapterMasterDetector.ts.
 */

import type { Subject } from '../../shared/types';
import { CURRICULUM } from '../curriculum';

/**
 * Per-KP metadata inside a chapter map entry. `excluded` lets a future
 * `tag: 'extension'` KP opt out of chapter-mastered judgement without
 * touching the detector.
 */
export interface ChapterKpRef {
  kpId: string;
  excluded: boolean;
}

/** Per-chapter aggregate used by chapter-master detector. */
export interface ChapterMapEntry {
  chapterId: string;          // '{subject}.ch{number}'
  subject: Subject;
  /** Display title (subject curriculum chapter `title`, no prefix-strip). */
  title: string;
  kpRefs: ChapterKpRef[];
}

/**
 * Strip kid-facing markup prefixes from a raw chapter title before sending it
 * to a LINE push: e.g. `「【三下】📚 段考真題庫(綜合)」` → `「段考真題庫(綜合)」`.
 * Pure helper; safe to call on already-stripped strings (idempotent).
 */
export function stripChapterTitlePrefix(raw: string): string {
  // 1) drop a leading `【…】` block (with optional 📚/📖 + spaces after).
  let s = raw.replace(/^【[^】]*】\s*[📚📖]?\s*/u, '');
  // 2) drop a stray leading `📚`/`📖` (no 【】 wrapper) — also kid-facing only.
  s = s.replace(/^[📚📖]\s*/u, '');
  return s.trim();
}

/** Stable chapter id format used everywhere in W4. */
export function chapterIdFor(subject: Subject, number: number): string {
  return `${subject}.ch${number}`;
}

/**
 * Build the static map. Exported as a function ONLY for tests that want to
 * re-derive after mocking CURRICULUM; production code should use the
 * `CHAPTER_KP_MAP` constant below.
 */
export function buildChapterKpMap(): Record<string, ChapterMapEntry> {
  const out: Record<string, ChapterMapEntry> = {};
  for (const sub of CURRICULUM) {
    for (const chapter of sub.chapters) {
      const id = chapterIdFor(sub.subject, chapter.number);
      out[id] = {
        chapterId: id,
        subject: sub.subject,
        title: chapter.title,
        // Every CURRICULUM KP is required until a future `tag: 'extension'`
        // marker exists. excluded=false keeps the schema forward-compatible.
        kpRefs: chapter.kps.map(kp => ({ kpId: kp.id, excluded: false })),
      };
    }
  }
  return out;
}

/** Build-time static map: chapterId → ChapterMapEntry. */
export const CHAPTER_KP_MAP: Record<string, ChapterMapEntry> = buildChapterKpMap();

/**
 * Reverse lookup: kpId → chapterId. Useful when academy.ts wants to find
 * the chapter affected by a single KP update without walking CURRICULUM.
 * Built once at module load. Returns undefined for unknown kpIds (e.g.
 * meow911 KPs that haven't been wired into CURRICULUM yet).
 */
export const KP_TO_CHAPTER: Record<string, string> = (() => {
  const out: Record<string, string> = {};
  for (const entry of Object.values(CHAPTER_KP_MAP)) {
    for (const ref of entry.kpRefs) {
      // First-wins on collisions; CURRICULUM shouldn't have duplicate kpIds
      // across chapters, but if it does we keep the earlier mapping for
      // deterministic behaviour.
      if (!(ref.kpId in out)) out[ref.kpId] = entry.chapterId;
    }
  }
  return out;
})();

/** Convenience: lookup the chapter entry for a given kpId, or undefined. */
export function chapterForKp(kpId: string): ChapterMapEntry | undefined {
  const cid = KP_TO_CHAPTER[kpId];
  return cid ? CHAPTER_KP_MAP[cid] : undefined;
}
